import type { WorkRow } from '../work-row'
import { platformFor } from '../platforms'

type Props = { row: WorkRow }

/**
 * The `scrape_comment_fetch` params as the Rust `ScrapeCommentFetch` struct stores them.
 * `since` is the field's old name; rows written under it are not rewritten.
 */
type ScrapeCommentFetchParams = {
    platform?: string
    service_account_username?: string
    mode?: 'profile' | 'posts'
    window_days?: number | null
    from?: string | null
    since?: string | null
    to?: string | null
    targets?: string[]
    scrape_profile?: string | null
}

/** The backend's default for profile mode when no window is stored. */
// Mirrors DEFAULT_PROFILE_WINDOW_DAYS in services/libs/jobs/src/scrape_comment_fetch/mod.rs;
// change both or the panel describes a window the run does not use.
const DEFAULT_PROFILE_WINDOW_DAYS = 2

// Exported for the test that checks it against the Rust summary's cases.
export const windowText = (p: ScrapeCommentFetchParams): string | null => {
    const from = p.from ?? p.since
    // `!= null`, not truthiness: the Rust summary treats a stored 0 as a
    // window too, and the two must agree on what a row says about itself.
    if (p.window_days != null) return `Last ${p.window_days} days`
    if (from && p.to) return `${from} to ${p.to}`
    if (from) return `From ${from}`
    if (p.mode === 'profile') return `Last ${DEFAULT_PROFILE_WINDOW_DAYS} days`
    // YouTube posts mode takes no window: the targeted fetch collects each
    // video's whole comment set.
    return null
}

/**
 * A Monitor's settings in plain words: what it watches, how far back, and
 * the run budget. Read straight from the row's params so the panel needs
 * no extra query.
 */
function ScrapeCommentFetchDetails({ row }: Props) {
    const p = (row.params ?? {}) as ScrapeCommentFetchParams
    const platform = p.platform ? platformFor(p.platform) : undefined
    const window = windowText(p)
    const targets = p.targets ?? []

    return (
        <dl className="text-sm space-y-2">
            <div>
                <dt className="text-xs text-ink-500">Account</dt>
                <dd className="text-ink-900">
                    {platform?.label ?? p.platform ?? '—'} · {p.service_account_username ?? '—'}
                </dd>
            </div>
            <div>
                <dt className="text-xs text-ink-500">Mode</dt>
                <dd className="text-ink-900">
                    {p.mode === 'posts' ? 'Specific posts' : 'Whole profile'}
                </dd>
            </div>
            {window ? (
                <div>
                    <dt className="text-xs text-ink-500">Window</dt>
                    <dd className="text-ink-900">{window}</dd>
                </div>
            ) : null}
            <div>
                <dt className="text-xs text-ink-500">Run budget</dt>
                {/* 'daily' mirrors the None arm of the scrape_profile match in
                    services/libs/jobs/src/scrape_comment_fetch/mod.rs (ScrapeCommentFetch::validate). */}
                <dd className="text-ink-900">{p.scrape_profile ?? 'daily'}</dd>
            </div>
            {p.mode === 'posts' ? (
                <div>
                    <dt className="text-xs text-ink-500">
                        {platform?.targetsLabel ?? 'Targets'} ({targets.length})
                    </dt>
                    <dd>
                        {targets.length === 0 ? (
                            <span className="text-ink-500">None yet.</span>
                        ) : (
                            <ul className="mt-1 space-y-0.5 font-mono text-xs text-ink-900 break-all">
                                {targets.map((target) => (
                                    <li key={target}>{target}</li>
                                ))}
                            </ul>
                        )}
                    </dd>
                </div>
            ) : null}
        </dl>
    )
}

export default ScrapeCommentFetchDetails
